// React
import React, { useMemo, useState } from 'react'

// Next
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { GetStaticProps, InferGetStaticPropsType } from 'next'

// Components
const MemeEditor = dynamic(
  () => import('@/components/memes/editor').then((mod) => mod.MemeEditor),
  { ssr: false }
)
import { LoginButton } from '@/components/blocks/login-button'
import { CustomLoader } from '@/components/blocks/custom-loader'
import { DefaultSEO } from '@/components/seo/DefaultSEO'

// Utils
import { useSession, useWatermark } from '@/utils/auth/user-context'
import { getScaledDownPublicImageDimensions } from '@/utils/meme/image'
import { fetchMemeMetadata } from '@/utils/text-to-meme-2/metadata'
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'
import { logger } from '@/utils/ui/logger'

// Types
import {
  BaseCaption,
  Caption,
  IPositionConfig,
  MemeConstants,
} from '@supermeme-ai/types'

// External
import { useWindowSize } from 'react-use'
import { isDesktop } from 'react-device-detect'
import { RocketIcon } from 'lucide-react'
import { toBase64 } from 'js-base64'
import { useTranslation } from 'next-i18next'

const MemeEditorPage: React.FC = ({
  imageSrc,
  pageTitle,
  imageName,
  imageDescription,
  imageWidth,
  imageHeight,
  initialCaptions,
  memeOrigin,
  memeDescription,
  memeExamples,
  relatedMemes,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('routes/meme/image')
  const { t: tEditor } = useTranslation('components/meme-editor')
  const router = useRouter()

  // context
  const [watermark] = useWatermark()
  const session = useSession()

  // page state
  const [captions, setCaptions] = useState<Caption[]>(initialCaptions)
  const [header, setHeader] = useState<BaseCaption>({
    text: '',
    fontSize: MemeConstants.headerFooter.initialFontSize,
    fontColor: MemeConstants.headerFooter.fontColor,
    fontFamily: MemeConstants.headerFooter.fontFamily,
  })
  const [footer, setFooter] = useState<BaseCaption>({
    text: '',
    fontSize: MemeConstants.headerFooter.initialFontSize,
    fontColor: MemeConstants.headerFooter.fontColor,
    fontFamily: MemeConstants.headerFooter.fontFamily,
  })
  const [resultId, setResultId] = useState<number>(0)

  // other
  const { width: screenWidth } = useWindowSize()
  const maxDimension = useMemo(
    () => (screenWidth >= 500 ? 500 : window.innerWidth - 32),
    [screenWidth]
  )

  const parameter = useMemo(
    () =>
      toBase64(
        JSON.stringify({
          meme_text: (imageDescription || '').replace("'", "'"),
          image_path: (imageName || '').replace("'", "'"),
          description: (imageDescription || '').replace("'", "'"),
        })
      ),
    [imageName, imageDescription]
  )

  const renderEditor = useMemo(
    () => (
      <MemeEditor
        captions={captions}
        setCaptions={setCaptions}
        watermark={watermark}
        maxDimension={maxDimension}
        isDesktop={isDesktop}
        header={header}
        setHeader={setHeader}
        footer={footer}
        setFooter={setFooter}
        resultId={resultId}
        setResultId={setResultId}
        imageSrc={imageSrc}
        memeFormat={'image'}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        isStarredMeme={false}
        starUnstarCallback={() => {}}
        t={tEditor}
      />
    ),
    [
      captions,
      watermark,
      maxDimension,
      header,
      footer,
      imageSrc,
      imageWidth,
      imageHeight,
      resultId,
      setResultId,
      tEditor,
    ]
  )

  const title = useMemo(() => {
    if (!pageTitle) return ''
    return pageTitle
      .replace(/\.[^/.]+$/, '') // Remove extension
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }, [pageTitle])

  if (router.isFallback) {
    return (
      <div className='flex items-center justify-center'>
        <CustomLoader />
      </div>
    )
  }

  return (
    <>
      <DefaultSEO
        title={t('meta.title', { title })}
        description={t('meta.description', {
          title,
          description: imageDescription || '',
        })}
        url={`https://supermeme.ai/meme/${imageName}-meme-template`}
        ogImage={imageSrc}
      />

      <div className='space-y-8'>
        <div className='flex flex-col items-center justify-center px-2'>
          {renderEditor}
        </div>

        {!session && (
          <>
            <div className='flex items-center justify-center'>
              <div className='bg-slate-50 border-slate-200 mx-auto grid w-full max-w-4xl grid-cols-1 gap-y-4 rounded-lg border p-6 shadow-sm lg:grid-cols-12 lg:gap-x-4 lg:gap-y-0'>
                <div className='col-span-7'>
                  <p className='text-center font-semibold lg:text-left'>
                    {t('callToAction.title.line1')}
                  </p>
                  <p className='text-center font-semibold lg:text-left'>
                    {t('callToAction.title.line2')}
                  </p>
                </div>
                <div className='col-span-1 hidden lg:block'></div>
                <LoginButton
                  buttonText={t('callToAction.button')}
                  buttonIcon={<RocketIcon className='mr-2 h-4 w-4' />}
                  className='col-span-4 w-full lg:w-auto'
                  redirectUrl={`/meme-generator?template=${parameter}&uploadedTemplate=false`}
                />
              </div>
            </div>
          </>
        )}

        {/* Meme Information Sections */}
        <div className='mx-auto max-w-4xl space-y-8 px-4'>
          {/* Meme Origin - Only show if content exists */}
          {memeOrigin && (
            <div className='space-y-2'>
              <h3 className='text-xl font-semibold text-gray-900'>
                {title} Meme Template Origin
              </h3>
              <p className='whitespace-pre-line text-gray-700'>{memeOrigin}</p>
            </div>
          )}

          {/* Meme Description - Only show if content exists */}
          {memeDescription && (
            <div className='space-y-2'>
              <h3 className='text-xl font-semibold text-gray-900'>
                {title} Meme Template Description
              </h3>
              <p className='text-gray-700'>{memeDescription}</p>
            </div>
          )}

          {/* Meme Examples - Only show if there are examples */}
          {memeExamples.length > 0 && (
            <div className='space-y-2'>
              <h3 className='text-xl font-semibold text-gray-900'>
                Example {title} Meme Usage
              </h3>
              <p className='mb-2 text-gray-700'>
                Some common scenarios for which this meme would be appropriate:
              </p>
              <ul className='list-disc space-y-1 pl-5'>
                {memeExamples.map((example, index) => (
                  <li key={index} className='text-gray-700'>
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Related Memes Section - Only show if there are related memes */}
        {relatedMemes.length > 0 && (
          <div className='bg-slate-50 py-8'>
            <div className='mx-auto max-w-4xl space-y-4 px-4'>
              <h3 className='text-xl font-semibold text-gray-900'>
                Related Meme Templates for {title}
              </h3>
              <div className='flex flex-col space-y-2'>
                {relatedMemes.map((meme, index) => (
                  <Link
                    key={index}
                    href={`/meme/${meme.path.replace(/\.[^/.]+$/, '')}-meme-template`}
                    className='text-indigo-600 underline hover:text-indigo-800'
                  >
                    {meme.templateName}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export async function getStaticPaths() {
  // Fetch a list of all image names and metadata
  const memeTemplates = await fetchMemeMetadata('', 2000, {
    type: { $in: ['memedescription'] },
  })

  const paths = memeTemplates.map((template) => ({
    params: {
      // Remove file extension from the URL path and add -meme-template suffix
      image: template.image_path.replace(/\.[^/.]+$/, '') + '-meme-template',
    },
  }))

  return { paths, fallback: true }
}

type PropsData = {
  imageSrc: string
  pageTitle: string
  imageName: string
  imageDescription: string
  imageWidth: number
  imageHeight: number
  initialCaptions: Caption[]
  memeOrigin?: string
  memeDescription?: string
  memeExamples: string[]
  relatedMemes: Array<{ templateName: string; path: string }>
}

export const getStaticProps: GetStaticProps<PropsData> = async ({
  params,
  locale,
}) => {
  // Strip off the -meme-template suffix for data fetching
  const imageParam = (params.image as string).replace('-meme-template', '')
  // First try to find the exact match without extension
  let memeTemplates = await fetchMemeMetadata(imageParam, 1, {
    image_path: { $eq: imageParam },
  })

  if (!memeTemplates.length || memeTemplates.length == 0) {
    // Try with common extensions as fallback
    const extensionsList = ['.png', '.jpg', '.jpeg', '.gif']
    for (const ext of extensionsList) {
      const withExtension = `${imageParam}${ext}`
      const templatesWithExt = await fetchMemeMetadata(withExtension, 1, {
        image_path: { $eq: withExtension },
      })

      if (templatesWithExt.length > 0) {
        memeTemplates.push(...templatesWithExt)
        break
      }
    }
  }

  if (!memeTemplates.length || memeTemplates.length == 0) {
    logger.warn(`Image not found: ${imageParam}`, {
      image: imageParam,
      locale,
    })
    return {
      notFound: true,
    }
  }

  const memeTemplateRichData = require('./data/meme_templates.json').memes || []
  // Find the template either by exact match or by removing extension from path
  const richTemplate = memeTemplateRichData.find(
    (t) =>
      t.path === imageParam || t.path.replace(/\.[^/.]+$/, '') === imageParam
  )
  const template = {
    // Find the template by either exact match or path without extension
    ...memeTemplates.find((t) => {
      const pathWithoutExt = t.image_path.replace(/\.[^/.]+$/, '')
      return t.image_path === imageParam || pathWithoutExt === imageParam
    }),
    description: richTemplate?.description || '',
    origin: richTemplate?.origin || '',
    exampleScenarios: richTemplate?.exampleScenarios || [],
    relatedMemes: richTemplate?.relatedMemes || [],
  }

  // Get the actual image path with extension
  const actualImagePath = (template.image_path || imageParam) as string

  // Fetch all image names and metadata
  const { imageUrl, imageDimensions } =
    await getScaledDownPublicImageDimensions(actualImagePath, 500)
  const imageSrc = imageUrl

  const positionsConfig = JSON.parse(
    template.positions_config || '[]'
  ) as IPositionConfig[]

  const landingProps = await getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/meme/image',
      'components/login-form',
      'components/meme-editor',
    ],
  })

  return {
    ...landingProps,
    props: {
      imageSrc: imageSrc,
      pageTitle: template?.name || imageParam,
      imageName: imageParam,
      imageDescription: template?.description || '',
      imageWidth: imageDimensions.width,
      imageHeight: imageDimensions.height,
      initialCaptions: positionsConfig.map((config) => ({
        x: Math.floor(config.relativeX * imageDimensions.width),
        y: Math.floor(config.relativeY * imageDimensions.height),
        text: 'Meme text goes here',
        width: Math.floor(config.relativeWidth * imageDimensions.width),
        height: Math.floor(config.relativeHeight * imageDimensions.height),
        fontSize: MemeConstants.initialFontSizeV2,
        fontFamily: null,
        language: 'en',
        rotateAngle: config.rotateAngle,
      })),
      memeOrigin: template?.origin || '',
      memeDescription: template?.description || '',
      memeExamples: template?.exampleScenarios || [],
      relatedMemes: template?.relatedMemes || [],
      ...landingProps.props,
    },
    revalidate: 3600,
  }
}

export default MemeEditorPage
